import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  toast
} from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import { URL_ROUTES } from '@/constants';
import { addToSaveForLater } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { CartOutOfStockItem } from '@/modules/cart/domain/CartOutOfStockItem';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendBuyNowEvents';
import {
  buildEventOutOfStock,
  CLOSE_MODAL_EVENT,
  EXPLORE_PRODUCTS_EVENT,
  HEADER_TITLE,
  LABEL_EXPLORE_PRODUCTS,
  LABEL_SAVE,
  SAVE_PRODUCT_EVENT,
  TOAST_SAVE_ERROR,
  TOAST_SAVE_SUCCESS
} from '@/Pages/BuyNowPage/OutOfStock/constants';
import OutOfStockInfo from '@/Pages/BuyNowPage/OutOfStock/OutOfStockInfo';
import OutOfStockProductItem from '@/Pages/BuyNowPage/OutOfStock/OutOfStockProductItem';

const loadHome = () => {
  const isWindowDefined = typeof window !== 'undefined';
  if (!isWindowDefined) return;

  const originURL = isWindowDefined ? window.location.origin : '';
  const homeURL = `${originURL}${URL_ROUTES.HOME}`;
  window.location.href = homeURL;
};

export const ModalOutOfStock = ({
  product
}: {
  product: CartOutOfStockItem;
}) => {
  const dispatch = useAppDispatch();
  const [isSavingProducts, setIsSavingProducts] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [hasClickSaved, setHasClickSaved] = useState(false);

  const onClose = () => {
    sendEvent(CLOSE_MODAL_EVENT);
    setIsModalOpen(false);
    loadHome();
  };

  const handleExploreProducts = () => {
    sendEvent(EXPLORE_PRODUCTS_EVENT);
    setIsModalOpen(false);
    loadHome();
  };

  const handleSave = async () => {
    try {
      sendEvent(SAVE_PRODUCT_EVENT);

      setHasClickSaved(true);
      setIsSavingProducts(true);

      await dispatch(addToSaveForLater({ sku: product?.sku }));

      setIsSavingProducts(false);

      toast({
        ...TOAST_SAVE_SUCCESS,
        onClose: () => {
          loadHome();
        }
      });
    } catch (error) {
      setIsSavingProducts(false);
      toast(TOAST_SAVE_ERROR);
    }
  };

  const outOfStockEvent = useMemo(() => {
    const event = buildEventOutOfStock(product);
    return event;
  }, [product]);

  useEffect(() => {
    sendEvent(outOfStockEvent);
  }, [outOfStockEvent]);

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal isOpen={isModalOpen} size="sm">
      <ModalHeader
        data-testid="out-of-stock-modal-header"
        icon={{
          name: 'il-product-cancelled'
        }}
        title={HEADER_TITLE}
        onClose={onClose}
      />

      <ModalBody
        data-testid="out-of-stock-modal-body"
        className="co-flex co-flex-col co-gap-4"
      >
        <OutOfStockProductItem product={product} />
        <OutOfStockInfo />
      </ModalBody>

      <ModalFooter className="co-flex co-flex-col-reverse tablet:co-flex-row co-justify-end co-gap-2">
        <Button
          variant="secondary"
          onClick={handleSave}
          className="co-w-full tablet:co-w-auto"
          loading={isSavingProducts}
          disabled={isSavingProducts || hasClickSaved}
        >
          {LABEL_SAVE}
        </Button>
        <Button
          variant="primary"
          onClick={handleExploreProducts}
          className="co-w-full tablet:co-w-auto"
        >
          {LABEL_EXPLORE_PRODUCTS}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ModalOutOfStock.displayName = 'ModalOutOfStock';

export default ModalOutOfStock;
